import { addToast } from 'core/notifications';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import kebabCase from 'lodash/kebabCase';
import axios from '@/util/axios';

import global from './i18nStrings.json';

const convertKeysToKebabCase = (object) =>
  Object.fromEntries(
    Object.entries(object || {}).map(([snakeCase, value]) => [
      kebabCase(snakeCase).replace(/-plural$/, '_plural'), // preserve pluralisation
      value,
    ]),
  );

/**
 * Initializes i18next with translations from the server.
 *
 * @async
 * @param {Object} [options={}] - Configuration options
 * @param {string} [options.key] - Translation key to load into the 'app' namespace (e.g., 'courses', 'my_learning').
 * @param {boolean} [options.shouldLoadTranslations] - Load translations even without a key (e.g., for AI CTA).
 * @param {boolean} [options.shouldPutAllTranslationsInResources] - Load all translations into separate namespaces (for SPAs).
 * @param {Object} [options.strings] - Locally created and stored keys, untranslated and use on the Manage app.
 * @returns {Promise<void>}
 * @example
 * // SPA: Load all translations into namespaces
 * await initI18n({ shouldPutAllTranslationsInResources: true });
 *
 * @example
 * // Feature page: Load specific translations into 'app' namespace
 * await initI18n({ key: 'courses' });
 *
 * @example
 * // No specific key, but need translations (e.g., AI CTA)
 * await initI18n({ shouldLoadTranslations: true });
 */
export default async ({
  key,
  shouldLoadTranslations,
  shouldPutAllTranslationsInResources = false,
  strings,
} = {}) => {
  const ns = ['app', 'global'];
  const lng = document.documentElement.lang;
  const fallbackLng = 'en';
  const resources = {
    [lng]: { app: strings || {}, global },
    [fallbackLng]: { app: strings || {}, global },
  };
  const translationPaths =
    (key || shouldLoadTranslations || shouldPutAllTranslationsInResources) &&
    window.Intellum?.translation_paths;

  if (translationPaths) {
    await Promise.all(
      Object.entries(resources).map(async ([locale, resource]) => {
        const translationPath = translationPaths[locale];

        if (!translationPath) return;

        try {
          const {
            data: { [locale]: data },
          } = await axios.get(translationPath);

          const localeResources = { ...resource };

          if (shouldPutAllTranslationsInResources) {
            Object.keys(data).forEach((dataKey) => {
              if (dataKey !== 'global' && !ns.includes(dataKey)) {
                ns.push(dataKey);
              }

              const convertedData = convertKeysToKebabCase(data[dataKey]);
              if (dataKey === 'global') {
                localeResources.global = { ...localeResources.global, ...convertedData };
              } else {
                localeResources[dataKey] = convertedData;
              }
            });
          }

          if (key) {
            localeResources.app = {
              ...localeResources.app,
              ...convertKeysToKebabCase(data[key]),
            };
          }

          if (!shouldPutAllTranslationsInResources) {
            localeResources.global = {
              ...localeResources.global,
              ...convertKeysToKebabCase(data.global),
            };
          }

          resources[locale] = localeResources;
        } catch {
          addToast('Error loading translations.', 'error');
        }
      }),
    );
  }

  i18n.use(initReactI18next).init({
    debug: process.env.NODE_ENV === 'development',
    defaultNS: ns[0],
    fallbackLng,
    fallbackNS: ns,
    interpolation: { escapeValue: false, prefix: '%{', suffix: '}' },
    lng,
    ns,
    resources,
  });
};
